import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export interface StudentScope {
  studentId: string;
  enrollmentId: string | null;
  classSectionId: string | null;
  academicYearId: string | null;
  className?: string;
  sectionName?: string;
  rollNumber?: string;
}

export interface TeacherScope {
  teacherId: string;
  classSectionIds: string[];
  classSections: Array<{
    classSectionId: string;
    className: string;
    sectionName: string;
    studentCount: number;
  }>;
}

export interface GuardianWard {
  studentId: string;
  studentName: string;
  admissionNumber: string;
  className: string | null;
  sectionName: string | null;
  rollNumber: string | null;
  classSectionId: string | null;
  enrollmentId: string | null;
}

export interface GuardianScope {
  studentIds: string[];
  classSectionIds: string[];
  wards: GuardianWard[];
}

export interface RoleScope {
  roles: string[];
  student?: StudentScope;
  teacher?: TeacherScope;
  guardian?: GuardianScope;
}

let cachedScope: RoleScope | null = null;
let cacheUserId: string | null = null;

/**
 * Fetches and caches the role-specific scope for the current user.
 * Students get their classSectionId/enrollmentId, teachers get their assigned classSectionIds,
 * guardians get their wards' info.
 */
export function useRoleScope() {
  const { user } = useAuth();
  const [scope, setScope] = useState<RoleScope | null>(
    user && cachedScope && cacheUserId === user.userId ? cachedScope : null,
  );
  const [loading, setLoading] = useState(!scope);

  const fetchScope = useCallback(async () => {
    if (!user) { setScope(null); setLoading(false); return; }
    // Return cache if same user
    if (cachedScope && cacheUserId === user.userId) {
      setScope(cachedScope);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getMyScope();
      cachedScope = data;
      cacheUserId = user.userId;
      setScope(data);
    } catch (err) {
      console.error('Failed to fetch role scope:', err);
      setScope(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchScope();
  }, [fetchScope]);

  const isAdmin = user?.roles?.some(r => ['super_admin', 'school_admin'].includes(r)) ?? false;
  const isTeacher = user?.roles?.includes('teacher') ?? false;
  const isStudent = user?.roles?.includes('student') ?? false;
  const isGuardian = user?.roles?.includes('guardian') ?? false;

  /** Get the class-section IDs this user is allowed to see */
  const getAllowedClassSectionIds = (): string[] | null => {
    if (isAdmin) return null; // no restriction
    if (isTeacher && scope?.teacher) return scope.teacher.classSectionIds;
    if (isStudent && scope?.student?.classSectionId) return [scope.student.classSectionId];
    if (isGuardian && scope?.guardian) return scope.guardian.classSectionIds;
    return [];
  };

  return {
    scope,
    loading,
    isAdmin,
    isTeacher,
    isStudent,
    isGuardian,
    getAllowedClassSectionIds,
    refreshScope: fetchScope,
  };
}

/** Invalidate the cache (e.g. on logout) */
export function clearRoleScopeCache() {
  cachedScope = null;
  cacheUserId = null;
}
